/**
 * ParlayAnalysisSection Component
 * Displays AI analysis results for a parlay with verdict, reasoning,
 * correlation warnings, and actionable suggestions
 */

import type { BuilderLeg } from '@parlay-picker/shared/builderLeg';
import type { ActionableSuggestion, ParlayAnalysis } from '@parlay-picker/shared/parlayAnalysis';
import { cn } from '@utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronUp, X } from 'lucide-react';
import { type ReactElement, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

interface ParlayAnalysisSectionProps {
  analysis: ParlayAnalysis;
  legs: BuilderLeg[];
  onApplySuggestion: (suggestion: ActionableSuggestion) => void;
  onClose: () => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get styling for verdict badge
 */
function getVerdictStyle(
  verdict: ParlayAnalysis['verdict'],
  rebrand: boolean,
): {
  bg: string;
  border: string;
  text: string;
  label: string;
  tooltip: string;
} {
  if (rebrand) {
    switch (verdict) {
      case 'good':
        return {
          bg: 'bg-theme-success/10',
          border: 'border-theme-success/30',
          text: 'text-theme-success',
          label: 'Good',
          tooltip: 'Strong edges across legs, minimal correlation, good game script diversity',
        };
      case 'okay':
        return {
          bg: 'bg-theme-warning/10',
          border: 'border-theme-warning/30',
          text: 'text-theme-warning',
          label: 'Okay',
          tooltip: 'Decent edges but some concerns (moderate correlation, some B2B, etc.)',
        };
      case 'risky':
        return {
          bg: 'bg-orange-500/10',
          border: 'border-orange-500/30',
          text: 'text-orange-400',
          label: 'Risky',
          tooltip: 'Significant concerns (high correlation, multiple B2B, blowout risks)',
        };
      case 'bad':
        return {
          bg: 'bg-theme-danger/10',
          border: 'border-theme-danger/30',
          text: 'text-theme-danger',
          label: 'Bad',
          tooltip:
            'Major red flags (opposite sides of same game, extremely correlated, multiple high-risk factors)',
        };
    }
  }

  switch (verdict) {
    case 'good':
      return {
        bg: 'bg-green-900/30',
        border: 'border-green-600/50',
        text: 'text-green-400',
        label: 'Good',
        tooltip: 'Strong edges across legs, minimal correlation, good game script diversity',
      };
    case 'okay':
      return {
        bg: 'bg-yellow-900/30',
        border: 'border-yellow-600/50',
        text: 'text-yellow-400',
        label: 'Okay',
        tooltip: 'Decent edges but some concerns (moderate correlation, some B2B, etc.)',
      };
    case 'risky':
      return {
        bg: 'bg-orange-900/30',
        border: 'border-orange-600/50',
        text: 'text-orange-400',
        label: 'Risky',
        tooltip: 'Significant concerns (high correlation, multiple B2B, blowout risks)',
      };
    case 'bad':
      return {
        bg: 'bg-red-900/30',
        border: 'border-red-600/50',
        text: 'text-red-400',
        label: 'Bad',
        tooltip:
          'Major red flags (opposite sides of same game, extremely correlated, multiple high-risk factors)',
      };
  }
}

/**
 * Get icon for suggestion type
 */
function getSuggestionIcon(type: ActionableSuggestion['type']): string {
  switch (type) {
    case 'swap':
      return '🔄';
    case 'remove':
      return '🗑️';
    case 'adjust_line':
      return '📐';
  }
}

/**
 * Get button label for suggestion type
 */
function getSuggestionButtonLabel(type: ActionableSuggestion['type']): string {
  switch (type) {
    case 'swap':
      return 'Swap';
    case 'remove':
      return 'Remove';
    case 'adjust_line':
      return 'Adjust';
  }
}

// =============================================================================
// Component
// =============================================================================

/**
 * Resolve which leg a suggestion targets
 */
function getAffectedLeg(
  suggestion: ActionableSuggestion,
  legs: BuilderLeg[],
): { leg: BuilderLeg; displayIndex: number } | null {
  // Prefer legId match
  if (suggestion.legId) {
    const idx = legs.findIndex((l) => l.id === suggestion.legId);
    if (idx >= 0) return { leg: legs[idx], displayIndex: idx + 1 };
  }
  // Fallback to legIndex
  if (suggestion.legIndex >= 0 && suggestion.legIndex < legs.length) {
    return { leg: legs[suggestion.legIndex], displayIndex: suggestion.legIndex + 1 };
  }
  return null;
}

export function ParlayAnalysisSection({
  analysis,
  legs,
  onApplySuggestion,
  onClose,
}: ParlayAnalysisSectionProps): ReactElement {
  const [isExpanded, setIsExpanded] = useState(true);
  const verdictStyle = getVerdictStyle(analysis.verdict, true);

  /** Shared expandable content rendered by both rebrand and legacy branches */
  function renderExpandableContent(): ReactElement {
    return (
      <div className="px-3 pb-3 space-y-3">
        {/* Reasoning */}
        <div className="text-sm leading-relaxed text-theme-text-secondary">
          {analysis.reasoning}
        </div>

        {/* Correlation Warnings */}
        {analysis.correlationWarnings.length > 0 && (
          <div className="space-y-1">
            <div className="text-xs font-medium uppercase tracking-wider text-orange-400">
              Correlation Warnings
            </div>
            {analysis.correlationWarnings.map((warning, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-sm px-2 py-1.5 rounded text-orange-300 bg-orange-500/10"
              >
                <span className="shrink-0">⚠️</span>
                <span>{warning}</span>
              </div>
            ))}
          </div>
        )}

        {/* Actionable Suggestions */}
        {analysis.suggestions.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs font-medium uppercase tracking-wider text-theme-primary">
              Suggestions
            </div>
            {analysis.suggestions.map((suggestion, i) => {
              const affected = getAffectedLeg(suggestion, legs);
              const affectedLabel = affected
                ? `Leg ${affected.displayIndex}: ${affected.leg.player || affected.leg.team}`
                : null;

              return (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.01, x: 2 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  className="bg-theme-surface/50 border border-theme-border/30 px-3 py-2 rounded-lg"
                >
                  {affectedLabel && (
                    <div className="text-[10px] font-medium text-theme-text-tertiary uppercase tracking-wider mb-1">
                      {affectedLabel}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="shrink-0">{getSuggestionIcon(suggestion.type)}</span>
                      <span className="text-sm text-theme-text-secondary">{suggestion.text}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => onApplySuggestion(suggestion)}
                      className="shrink-0 px-3 py-1 text-xs font-medium bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill rounded-lg transition shadow-sm shadow-theme-primary/20"
                    >
                      {getSuggestionButtonLabel(suggestion.type)}
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* No suggestions message */}
        {analysis.suggestions.length === 0 && analysis.verdict === 'good' && (
          <div className="text-sm px-3 py-2 rounded flex items-center gap-2 text-theme-success bg-theme-success/10">
            <span>✅</span>
            <span>No changes needed - this parlay looks solid!</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn('mb-3 rounded-lg border overflow-hidden', verdictStyle.border, verdictStyle.bg)}
    >
      {/* Header - clickable to expand/collapse */}
      <div
        className="flex items-center justify-between px-3 py-2 cursor-pointer transition hover:bg-black/5"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🤖</span>
          <span className="text-sm font-medium text-theme-text">AI Analysis</span>
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded ${verdictStyle.text} bg-black/20 cursor-help`}
            title={verdictStyle.tooltip}
          >
            {verdictStyle.label}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Close button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="p-1 rounded transition text-theme-text-tertiary hover:text-theme-text hover:bg-theme-surface-hover"
            title="Dismiss analysis"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Expand/collapse chevron */}
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            <ChevronUp className="w-4 h-4 text-theme-text-tertiary" />
          </motion.div>
        </div>
      </div>

      {/* Expandable content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="analysis-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            {renderExpandableContent()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
